import {
  encodeRequestControl,
  encodeSimParameters,
  encodeSpinDownStart,
  encodeStart,
  encodeStop,
  encodeTargetPower,
  encodeTargetResistance,
  FTMS_BRIEF_OPCODES,
  FTMS_STANDARD_OPCODES,
  parseControlPointResponse,
  parseFitnessMachineFeature,
  parseFitnessMachineStatus,
  parseIndoorBikeData,
  parseSupportedInclinationRange,
  parseSupportedPowerRange,
  parseSupportedResistanceRange,
  type ControlPointResponse,
  type FitnessMachineStatus,
  type FtmsOpcodeProfile,
  type SpinDownState,
  type SupportedRange,
} from "./ftms";
import {
  VICTORY_MAX_GRADE_PCT,
  VICTORY_MIN_GRADE_PCT,
  type PowerSample,
  type TrainerDevice,
  type TrainerInfo,
  type TrainerTier,
} from "./types";

/** FTMS service and characteristic UUIDs used by the Victory. */
export const FTMS_SERVICE = "fitness_machine";
export const INDOOR_BIKE_DATA_UUID = 0x2ad2;
export const CONTROL_POINT_UUID = 0x2ad9;
export const STATUS_UUID = 0x2ada;
export const FITNESS_MACHINE_FEATURE_UUID = 0x2acc;
export const SUPPORTED_INCLINATION_RANGE_UUID = 0x2ad5;
export const SUPPORTED_RESISTANCE_RANGE_UUID = 0x2ad6;
export const SUPPORTED_POWER_RANGE_UUID = 0x2ad8;
export const DEVICE_INFORMATION_SERVICE = "device_information";
export const FIRMWARE_REVISION_UUID = 0x2a26;

// The subset of the Web Bluetooth API this package needs. Keeping our own
// structural types means the package builds without @types/web-bluetooth and
// tests can inject a fake GATT server.
export interface BluetoothRequestDeviceFilterLike {
  services?: Array<string | number>;
  name?: string;
  namePrefix?: string;
}

export interface BluetoothRequestDeviceOptionsLike {
  filters?: BluetoothRequestDeviceFilterLike[];
  optionalServices?: Array<string | number>;
  acceptAllDevices?: boolean;
}

export interface BleCharacteristicLike extends EventTarget {
  readonly value?: DataView;
  startNotifications(): Promise<BleCharacteristicLike>;
  stopNotifications(): Promise<BleCharacteristicLike>;
  readValue(): Promise<DataView>;
  writeValueWithResponse?(value: Uint8Array): Promise<void>;
  writeValue?(value: Uint8Array): Promise<void>;
}

export interface BleServiceLike {
  getCharacteristic(characteristic: string | number): Promise<BleCharacteristicLike>;
}

export interface BleGattServerLike {
  readonly connected: boolean;
  connect(): Promise<BleGattServerLike>;
  disconnect(): void;
  getPrimaryService(service: string | number): Promise<BleServiceLike>;
}

export interface BleDeviceLike extends EventTarget {
  readonly id: string;
  readonly name?: string;
  readonly gatt?: BleGattServerLike;
}

export interface WebBluetoothLike {
  requestDevice(options: BluetoothRequestDeviceOptionsLike): Promise<BleDeviceLike>;
}

export type TrainerErrorCode =
  | "unsupported"
  | "no-device"
  | "gatt"
  | "not-connected"
  | "control-unavailable"
  | "control-timeout"
  | "control-rejected"
  | "procedure-unsupported"
  | "spin-down-timeout"
  | "invalid-parameter";

export class TrainerError extends Error {
  constructor(
    readonly code: TrainerErrorCode,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = "TrainerError";
  }
}

export class TrainerControlError extends TrainerError {
  constructor(
    readonly requestOpcode: number,
    readonly resultCode: number,
    result: string,
  ) {
    super(
      result === "not-supported"
        ? "procedure-unsupported"
        : result === "invalid-parameter"
          ? "invalid-parameter"
          : "control-rejected",
      `Control point opcode 0x${requestOpcode.toString(16)} failed: ${result}`,
    );
    this.name = "TrainerControlError";
  }
}

export type ControlState = "idle" | "requesting" | "granted" | "failed" | "unavailable";
export type FeatureSupport = "supported" | "unsupported" | "unknown";
export type ActiveControl = "sim" | "target-power" | "resistance";
export type OpcodeProfileName = "standard" | "brief" | "unknown";

export interface TrainerCapabilities {
  control: FeatureSupport;
  simulation: FeatureSupport;
  targetPower: FeatureSupport;
  targetResistance: FeatureSupport;
  spinDown: FeatureSupport;
  /** Grade range this layer clamps commands to. */
  gradeRange: { minPct: number; maxPct: number };
  /** Ranges read from the trainer, when exposed. */
  inclinationRange?: SupportedRange;
  resistanceRange?: SupportedRange;
  powerRange?: SupportedRange;
}

export interface TrainerConnectionEvent {
  connected: boolean;
  reason: "connected" | "reconnect" | "disconnected" | "reconnect-failed" | "user";
}

export interface BleTrainerOptions {
  /** Injectable Web Bluetooth implementation; defaults to navigator.bluetooth. */
  bluetooth?: WebBluetoothLike;
  /** Injectable device chooser, mostly for tests. */
  requestDevice?: () => Promise<BleDeviceLike>;
  /** Defaults to the Victory profile (-10%). */
  minGradePct?: number;
  /** Defaults to the Victory profile (+16%). */
  maxGradePct?: number;
  /** How long to wait for a 0x80 control-point indication. Default 2000 ms. */
  controlTimeoutMs?: number;
  /**
   * Control-point opcode map. Default "standard" (FTMS 1.0, what the Victory
   * uses). "brief" selects the Phase 0 brief's non-standard numbers.
   */
  opcodeProfile?: "standard" | "brief";
  /** Reconnect automatically after an unexpected GATT disconnect. Default true. */
  autoReconnect?: boolean;
  /** Default 6. */
  reconnectAttempts?: number;
  /** Default 500 ms. */
  reconnectBaseDelayMs?: number;
  /** Default 5000 ms. */
  reconnectMaxDelayMs?: number;
  /** Trainer tier shown in the UI. The Victory is A. */
  tier?: TrainerTier;
  logger?: (message: string, error?: unknown) => void;
}

interface PendingResponse {
  opcode: number;
  resolve: (response: ControlPointResponse) => void;
  reject: (error: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface SpinDownWaiter {
  resolve: () => void;
  reject: (error: unknown) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * A fixed resistance level for a grade, used only when the trainer rejects
 * simulation parameters. It is an approximate feel fallback, not physics:
 * 0% -> level 0 and 16% -> level 80. The server still computes speed from
 * actual power, so the ride stays scoreable.
 */
export function resistanceForGrade(gradePct: number, levelPerPercent = 5): number {
  if (!Number.isFinite(gradePct)) return 0;
  return Math.max(0, Math.min(100, gradePct * levelPerPercent));
}

function getDefaultBluetooth(): WebBluetoothLike {
  const navigatorWithBluetooth = globalThis.navigator as
    | (Navigator & { bluetooth?: WebBluetoothLike })
    | undefined;
  const bluetooth = navigatorWithBluetooth?.bluetooth;
  if (!bluetooth) {
    throw new TrainerError(
      "unsupported",
      "Web Bluetooth is not available. Use Chrome or Edge on a secure origin (HTTPS or localhost).",
    );
  }
  return bluetooth;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function sameNumber(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

/**
 * JetBlack Victory (and any FTMS indoor bike) over Web Bluetooth.
 *
 * The power stream is established before control is requested. Control
 * failures are reported through onError and never stop power notifications, so
 * a ride remains scoreable with a power-only connection.
 */
export class BleTrainer implements TrainerDevice {
  private readonly requestDeviceFn: (() => Promise<BleDeviceLike>) | null;
  private readonly minGradePct: number;
  private readonly maxGradePct: number;
  private readonly controlTimeoutMs: number;
  private readonly opcodeProfileOption: "standard" | "brief";
  private readonly autoReconnect: boolean;
  private readonly reconnectAttempts: number;
  private readonly reconnectBaseDelayMs: number;
  private readonly reconnectMaxDelayMs: number;
  private readonly tier: TrainerTier;
  private readonly logger: (message: string, error?: unknown) => void;

  private bluetooth: WebBluetoothLike | null;
  private device: BleDeviceLike | null = null;
  private listenedDevice: BleDeviceLike | null = null;
  private gatt: BleGattServerLike | null = null;
  private indoorBikeData: BleCharacteristicLike | null = null;
  private controlPoint: BleCharacteristicLike | null = null;
  private statusCharacteristic: BleCharacteristicLike | null = null;

  private info: TrainerInfo | null = null;
  private connected = false;
  private connecting: Promise<TrainerInfo> | null = null;
  private explicitDisconnect = false;

  private readonly powerListeners = new Set<(sample: PowerSample) => void>();
  private readonly errorListeners = new Set<(error: TrainerError) => void>();
  private readonly statusListeners = new Set<(status: FitnessMachineStatus) => void>();
  private readonly connectionListeners = new Set<(event: TrainerConnectionEvent) => void>();
  private readonly spinDownWaiters = new Set<SpinDownWaiter>();
  /** Guards against attaching the same notification handler twice on reconnect. */
  private readonly listenedCharacteristics = new WeakSet<BleCharacteristicLike>();

  private controlQueue: Promise<void> = Promise.resolve();
  private pendingResponse: PendingResponse | null = null;
  private controlState: ControlState = "idle";
  private opcodes: FtmsOpcodeProfile = FTMS_STANDARD_OPCODES;
  private detectedOpcodeProfile: OpcodeProfileName = "unknown";
  private startAttempted = false;
  private machineState: "started" | "stopped" | "paused" | "unknown" = "unknown";
  private spinDownState: SpinDownState = "unknown";
  private simFailureCount = 0;

  private desiredGrade: number | null = null;
  private gradeInFlight = false;
  private activeControl: ActiveControl = "sim";
  private lastApplied: { mode: ActiveControl; value: number } | null = null;
  private lastTargetPower: number | null = null;
  private lastResistance: number | null = null;

  private capabilities: TrainerCapabilities;
  private lastError: TrainerError | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;

  constructor(options: BleTrainerOptions = {}) {
    this.requestDeviceFn = options.requestDevice ?? null;
    this.bluetooth = options.bluetooth ?? null;
    this.minGradePct = options.minGradePct ?? VICTORY_MIN_GRADE_PCT;
    this.maxGradePct = options.maxGradePct ?? VICTORY_MAX_GRADE_PCT;
    this.controlTimeoutMs = options.controlTimeoutMs ?? 2000;
    this.opcodeProfileOption = options.opcodeProfile ?? "standard";
    this.autoReconnect = options.autoReconnect ?? true;
    this.reconnectAttempts = options.reconnectAttempts ?? 6;
    this.reconnectBaseDelayMs = options.reconnectBaseDelayMs ?? 500;
    this.reconnectMaxDelayMs = options.reconnectMaxDelayMs ?? 5000;
    this.tier = options.tier ?? "A";
    this.logger = options.logger ?? (() => {});
    this.capabilities = {
      control: "unknown",
      simulation: "unknown",
      targetPower: "unknown",
      targetResistance: "unknown",
      spinDown: "unknown",
      gradeRange: { minPct: this.minGradePct, maxPct: this.maxGradePct },
    };
  }

  async connect(): Promise<TrainerInfo> {
    this.explicitDisconnect = false;
    this.cancelReconnect();
    this.reconnectAttempt = 0;
    return this.runConnect("connected");
  }

  /** Reconnect explicitly, e.g. from a "Retry" button after a dropout. */
  async reconnect(): Promise<TrainerInfo> {
    this.explicitDisconnect = false;
    this.cancelReconnect();
    this.reconnectAttempt = 0;
    return this.runConnect("reconnect");
  }

  async disconnect(): Promise<void> {
    this.explicitDisconnect = true;
    this.cancelReconnect();

    if (this.connecting) {
      try {
        await this.connecting;
      } catch {
        // The link is torn down below either way.
      }
    }

    const gatt = this.gatt;
    if (this.connected && gatt?.connected) {
      try {
        await this.enqueueControl(() => this.writeControl(encodeStop(false, this.opcodes.stop)));
      } catch (error) {
        this.report(error);
      }
    }

    await this.stopNotifications(this.indoorBikeData);
    await this.stopNotifications(this.controlPoint);
    await this.stopNotifications(this.statusCharacteristic);

    try {
      gatt?.disconnect();
    } catch (error) {
      this.log("GATT disconnect failed", error);
    }
    this.cleanupLink();
    this.emitConnection({ connected: false, reason: "user" });
  }

  onPower(callback: (sample: PowerSample) => void): () => void {
    this.powerListeners.add(callback);
    return () => this.powerListeners.delete(callback);
  }

  onError(callback: (error: TrainerError) => void): () => void {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  onStatus(callback: (status: FitnessMachineStatus) => void): () => void {
    this.statusListeners.add(callback);
    return () => this.statusListeners.delete(callback);
  }

  onConnection(callback: (event: TrainerConnectionEvent) => void): () => void {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  /**
   * Send a grade to the trainer. The caller applies trainer difficulty first.
   * This method intentionally resolves (not rejects) when control is missing:
   * power keeps flowing and a later reconnect can apply the latest grade.
   */
  async setGrade(gradePct: number): Promise<void> {
    const grade = this.clampGrade(Number.isFinite(gradePct) ? gradePct : 0);
    this.desiredGrade = grade;

    if (!this.connected || !this.controlPoint || this.capabilities.control === "unsupported") {
      return;
    }
    // A failed control request is not retried on every 4 Hz grade update.
    if (this.controlState === "failed") return;
    // Coalesce grade updates: the in-flight command reads desiredGrade when it
    // runs, so a slow control point cannot build a queue of stale grades.
    if (this.gradeInFlight) return;

    this.gradeInFlight = true;
    try {
      await this.enqueueControl(async () => {
        await this.ensureControlLocked();
        await this.ensureStartedLocked();
        await this.applyGradeLocked(this.desiredGrade ?? grade);
      });
    } catch (error) {
      this.report(error);
    } finally {
      this.gradeInFlight = false;
    }
  }

  /** ERG fallback: target power (0x05 standard). Explicit calls reject on failure. */
  async setTargetPower(watts: number): Promise<void> {
    if (!Number.isFinite(watts)) {
      throw new TrainerError("invalid-parameter", "Target power must be a finite number");
    }
    const value = Math.max(-32768, Math.min(32767, Math.round(watts)));
    try {
      await this.enqueueControl(async () => {
        await this.ensureControlLocked();
        await this.ensureStartedLocked();
        await this.writeTargetPowerLocked(value);
      });
    } catch (error) {
      this.report(error);
      throw error;
    }
    this.activeControl = "target-power";
    this.lastTargetPower = value;
    this.lastApplied = null;
  }

  /** Fixed resistance fallback: target resistance (0x04 standard), 0..100. */
  async setResistance(level: number): Promise<void> {
    if (!Number.isFinite(level)) {
      throw new TrainerError("invalid-parameter", "Resistance level must be a finite number");
    }
    const value = Math.max(0, Math.min(100, level));
    try {
      await this.enqueueControl(async () => {
        await this.ensureControlLocked();
        await this.ensureStartedLocked();
        await this.writeResistanceLocked(value);
      });
    } catch (error) {
      this.report(error);
      throw error;
    }
    this.activeControl = "resistance";
    this.lastResistance = value;
    this.lastApplied = { mode: "resistance", value };
  }

  /** Start an FTMS spin-down if the trainer supports it. */
  async calibrate(): Promise<void> {
    this.spinDownState = "requested";
    try {
      await this.enqueueControl(async () => {
        await this.ensureControlLocked();
        if (this.capabilities.spinDown === "unsupported") {
          throw new TrainerError(
            "procedure-unsupported",
            "This trainer does not support the FTMS spin-down procedure",
          );
        }
        let response: ControlPointResponse;
        try {
          response = await this.writeControl(encodeSpinDownStart(this.opcodes.spinDown));
        } catch (error) {
          if (
            error instanceof TrainerControlError &&
            (error.resultCode === 0x02 || error.resultCode === 0x03)
          ) {
            this.capabilities.spinDown = "unsupported";
          }
          throw error;
        }
        if (!response.success) {
          if (response.resultCode === 0x02 || response.resultCode === 0x03) {
            this.capabilities.spinDown = "unsupported";
          }
          throw new TrainerControlError(
            response.requestOpcode,
            response.resultCode,
            response.result,
          );
        }
      });
    } catch (error) {
      this.spinDownState = "unknown";
      this.report(error);
      throw error;
    }
  }

  /**
   * Wait for the spin-down success status notification. Spin-down can take
   * minutes; this is opt-in and not part of calibrate().
   */
  waitForSpinDown(timeoutMs = 120_000): Promise<void> {
    if (this.spinDownState === "success") return Promise.resolve();
    if (this.spinDownState === "error") {
      return Promise.reject(new TrainerError("control-rejected", "Spin-down reported an error"));
    }
    if (this.spinDownState === "stopped") {
      return Promise.reject(new TrainerError("control-rejected", "Spin-down was stopped"));
    }
    return new Promise<void>((resolve, reject) => {
      const waiter: SpinDownWaiter = { resolve, reject, timer: null };
      waiter.timer = setTimeout(() => {
        this.spinDownWaiters.delete(waiter);
        reject(new TrainerError("spin-down-timeout", "Timed out waiting for spin-down completion"));
      }, timeoutMs);
      this.spinDownWaiters.add(waiter);
    });
  }

  /** Retry a control request after a transient failure, then reapply the last command. */
  async requestControl(): Promise<void> {
    if (!this.connected) {
      throw new TrainerError("not-connected", "Trainer is not connected");
    }
    if (this.controlState === "failed") this.controlState = "idle";
    this.startAttempted = false;
    await this.enqueueControl(async () => {
      await this.ensureControlLocked();
      await this.ensureStartedLocked();
      await this.reapplyControlLocked();
    });
  }

  getInfo(): TrainerInfo | null {
    return this.info;
  }

  isConnected(): boolean {
    return this.connected;
  }

  getControlState(): ControlState {
    return this.controlState;
  }

  getActiveControl(): ActiveControl {
    return this.activeControl;
  }

  /** Which control-point opcode map was detected for this connection. */
  getOpcodeProfile(): OpcodeProfileName {
    return this.detectedOpcodeProfile;
  }

  getSpinDownState(): SpinDownState {
    return this.spinDownState;
  }

  getMachineState(): "started" | "stopped" | "paused" | "unknown" {
    return this.machineState;
  }

  getLastError(): TrainerError | null {
    return this.lastError;
  }

  getCapabilities(): TrainerCapabilities {
    return {
      ...this.capabilities,
      gradeRange: { ...this.capabilities.gradeRange },
      inclinationRange: this.capabilities.inclinationRange
        ? { ...this.capabilities.inclinationRange }
        : undefined,
      resistanceRange: this.capabilities.resistanceRange
        ? { ...this.capabilities.resistanceRange }
        : undefined,
      powerRange: this.capabilities.powerRange ? { ...this.capabilities.powerRange } : undefined,
    };
  }

  private async runConnect(
    reason: TrainerConnectionEvent["reason"],
  ): Promise<TrainerInfo> {
    if (this.connected && this.info) return this.info;
    if (this.connecting) return this.connecting;
    const attempt = this.connectInternal(reason);
    this.connecting = attempt;
    try {
      return await attempt;
    } finally {
      if (this.connecting === attempt) this.connecting = null;
    }
  }

  private async connectInternal(
    reason: TrainerConnectionEvent["reason"],
  ): Promise<TrainerInfo> {
    if (this.connected && this.info) return this.info;

    const device = this.device ?? (await this.requestDevice());
    this.device = device;
    this.attachDeviceListeners(device);

    const gatt = device.gatt;
    if (!gatt) {
      throw new TrainerError("gatt", "The selected device has no GATT server");
    }

    try {
      if (!gatt.connected) await gatt.connect();
      this.gatt = gatt;

      const service = await gatt.getPrimaryService(FTMS_SERVICE).catch((cause: unknown) => {
        throw new TrainerError(
          "gatt",
          "The device does not expose the FTMS service. Choose the trainer, not a sensor.",
          { cause },
        );
      });

      // Power first: subscribe and start notifications before any control.
      const indoorBikeData = await service
        .getCharacteristic(INDOOR_BIKE_DATA_UUID)
        .catch((cause: unknown) => {
          throw new TrainerError(
            "gatt",
            "The trainer does not expose Indoor Bike Data (0x2AD2)",
            { cause },
          );
        });
      this.attachValueListener(indoorBikeData, this.handleIndoorBikeData);
      await indoorBikeData.startNotifications();
      this.indoorBikeData = indoorBikeData;

      this.controlPoint = await this.setupControlPoint(service);
      this.statusCharacteristic = await this.setupStatus(service);
      this.capabilities.simulation = "unknown";
      this.capabilities.targetPower = "unknown";
      this.capabilities.targetResistance = "unknown";
      this.capabilities.spinDown = "unknown";
      await this.readCapabilities(service);

      const firmware = await this.readFirmwareRevision();
      this.info = {
        id: device.id,
        name: device.name?.trim() || "FTMS Trainer",
        firmware,
        tier: this.tier,
      };
      this.connected = true;
      this.controlState = this.controlPoint ? "idle" : "unavailable";
      this.lastError = null;
      this.emitConnection({ connected: true, reason });

      // Control is best-effort. A rejected request must not fail the connection.
      if (this.controlPoint) {
        try {
          await this.enqueueControl(async () => {
            await this.ensureControlLocked();
            await this.ensureStartedLocked();
            await this.reapplyControlLocked();
          });
        } catch (error) {
          this.report(error);
        }
      }

      return this.info;
    } catch (error) {
      this.cleanupLink();
      try {
        gatt.disconnect();
      } catch {
        // Already gone.
      }
      if (error instanceof TrainerError) throw error;
      throw new TrainerError("gatt", `Could not connect to the trainer: ${errorMessage(error)}`, {
        cause: error,
      });
    }
  }

  private async requestDevice(): Promise<BleDeviceLike> {
    if (this.requestDeviceFn) return this.requestDeviceFn();
    const bluetooth = this.bluetooth ?? getDefaultBluetooth();
    this.bluetooth = bluetooth;
    try {
      return await bluetooth.requestDevice({
        filters: [{ services: [FTMS_SERVICE] }],
        optionalServices: [DEVICE_INFORMATION_SERVICE],
      });
    } catch (cause) {
      throw new TrainerError(
        "no-device",
        "No trainer was selected, or the Bluetooth chooser was blocked. Click Connect again.",
        { cause },
      );
    }
  }

  private async setupControlPoint(service: BleServiceLike): Promise<BleCharacteristicLike | null> {
    let characteristic: BleCharacteristicLike;
    try {
      characteristic = await service.getCharacteristic(CONTROL_POINT_UUID);
    } catch (error) {
      this.capabilities.control = "unsupported";
      this.log("Trainer has no FTMS control point; continuing power-only", error);
      return null;
    }

    this.attachValueListener(characteristic, this.handleControlPoint);
    try {
      await characteristic.startNotifications();
      this.capabilities.control = "supported";
      return characteristic;
    } catch (error) {
      this.capabilities.control = "unsupported";
      this.log("Control point notifications are unavailable; continuing power-only", error);
      return null;
    }
  }

  private async setupStatus(service: BleServiceLike): Promise<BleCharacteristicLike | null> {
    try {
      const characteristic = await service.getCharacteristic(STATUS_UUID);
      this.attachValueListener(characteristic, this.handleStatus);
      await characteristic.startNotifications();
      return characteristic;
    } catch (error) {
      this.log("Fitness Machine Status is unavailable", error);
      return null;
    }
  }

  private async readCapabilities(service: BleServiceLike): Promise<void> {
    const featureView = await this.readOptional(service, FITNESS_MACHINE_FEATURE_UUID);
    if (featureView) {
      try {
        const feature = parseFitnessMachineFeature(featureView);
        // The target-setting mask is the second 32-bit word. If the trainer
        // only exposes the machine mask, leave target support unknown and probe.
        // A missing target bit is treated as unknown rather than unsupported so
        // quirky firmware still gets one chance to answer the actual opcode.
        if (featureView.byteLength >= 8) {
          this.capabilities.simulation = feature.targetSimulation ? "supported" : "unknown";
          this.capabilities.targetPower = feature.targetPower ? "supported" : "unknown";
          this.capabilities.targetResistance = feature.targetResistance
            ? "supported"
            : "unknown";
          // Do not start a calibration on a trainer that says it cannot spin down.
          this.capabilities.spinDown = feature.targetSpinDown ? "supported" : "unsupported";
        }
      } catch (error) {
        this.log("Could not parse Fitness Machine Feature", error);
      }
    }

    const inclinationView = await this.readOptional(service, SUPPORTED_INCLINATION_RANGE_UUID);
    if (inclinationView) {
      try {
        this.capabilities.inclinationRange = parseSupportedInclinationRange(inclinationView);
      } catch (error) {
        this.log("Could not parse supported inclination range", error);
      }
    }

    const resistanceView = await this.readOptional(service, SUPPORTED_RESISTANCE_RANGE_UUID);
    if (resistanceView) {
      try {
        this.capabilities.resistanceRange = parseSupportedResistanceRange(resistanceView);
      } catch (error) {
        this.log("Could not parse supported resistance range", error);
      }
    }

    const powerView = await this.readOptional(service, SUPPORTED_POWER_RANGE_UUID);
    if (powerView) {
      try {
        this.capabilities.powerRange = parseSupportedPowerRange(powerView);
      } catch (error) {
        this.log("Could not parse supported power range", error);
      }
    }
  }

  private async readFirmwareRevision(): Promise<string | undefined> {
    try {
      const service = await this.gatt?.getPrimaryService(DEVICE_INFORMATION_SERVICE);
      if (!service) return undefined;
      const characteristic = await service.getCharacteristic(FIRMWARE_REVISION_UUID);
      const value = await characteristic.readValue();
      const text = new TextDecoder().decode(value).replace(/\0/g, "").trim();
      return text || undefined;
    } catch (error) {
      this.log("Firmware revision is not exposed", error);
      return undefined;
    }
  }

  private async readOptional(
    service: BleServiceLike,
    characteristic: number,
  ): Promise<DataView | undefined> {
    try {
      const found = await service.getCharacteristic(characteristic);
      return await found.readValue();
    } catch {
      return undefined;
    }
  }

  private attachDeviceListeners(device: BleDeviceLike): void {
    if (this.listenedDevice === device) return;
    if (this.listenedDevice) {
      this.listenedDevice.removeEventListener(
        "gattserverdisconnected",
        this.handleGattDisconnected,
      );
    }
    device.addEventListener("gattserverdisconnected", this.handleGattDisconnected);
    this.listenedDevice = device;
  }

  private attachValueListener(
    characteristic: BleCharacteristicLike,
    handler: (value: DataView) => void,
  ): void {
    if (this.listenedCharacteristics.has(characteristic)) return;
    this.listenedCharacteristics.add(characteristic);
    characteristic.addEventListener("characteristicvaluechanged", (event: Event) => {
      const target = event.target as BleCharacteristicLike | null;
      const value = target?.value;
      if (value) handler(value);
    });
  }

  private readonly handleGattDisconnected = (): void => {
    if (this.explicitDisconnect) return;
    if (!this.connected && !this.gatt) return;
    this.cleanupLink();
    this.emitConnection({ connected: false, reason: "disconnected" });
    if (this.autoReconnect) this.scheduleReconnect();
  };

  private readonly handleIndoorBikeData = (value: DataView): void => {
    let data;
    try {
      data = parseIndoorBikeData(value);
    } catch (error) {
      this.log("Ignoring malformed Indoor Bike Data", error);
      return;
    }
    if (data.powerWatts === undefined) return;

    const sample: PowerSample = {
      t: Date.now(),
      watts: data.powerWatts,
      cadence: data.cadenceRpm ?? 0,
      speed: data.speedKmh,
    };
    for (const listener of [...this.powerListeners]) {
      try {
        listener(sample);
      } catch (error) {
        this.log("Power listener threw", error);
      }
    }
  };

  private readonly handleControlPoint = (value: DataView): void => {
    let response: ControlPointResponse;
    try {
      response = parseControlPointResponse(value);
    } catch (error) {
      this.log("Ignoring malformed control point response", error);
      return;
    }

    const pending = this.pendingResponse;
    if (pending && pending.opcode === response.requestOpcode) {
      clearTimeout(pending.timer);
      this.pendingResponse = null;
      pending.resolve(response);
      return;
    }
    this.log(
      `Ignoring control point response for opcode 0x${response.requestOpcode.toString(16)}`,
    );
  };

  private readonly handleStatus = (value: DataView): void => {
    let status: FitnessMachineStatus;
    try {
      status = parseFitnessMachineStatus(value);
    } catch (error) {
      this.log("Ignoring malformed Fitness Machine Status", error);
      return;
    }

    switch (status.type) {
      case "started":
        this.machineState = "started";
        break;
      case "stopped":
        this.machineState = "stopped";
        break;
      case "safety-stop":
        this.machineState = "stopped";
        break;
      case "paused":
        this.machineState = "paused";
        break;
      case "reset":
        this.machineState = "unknown";
        break;
      case "spin-down":
        this.spinDownState = status.status;
        this.resolveSpinDownWaiters(status.status);
        break;
      default:
        break;
    }

    for (const listener of [...this.statusListeners]) {
      try {
        listener(status);
      } catch (error) {
        this.log("Status listener threw", error);
      }
    }
  };

  private resolveSpinDownWaiters(state: SpinDownState): void {
    for (const waiter of [...this.spinDownWaiters]) {
      if (state === "success") {
        if (waiter.timer) clearTimeout(waiter.timer);
        this.spinDownWaiters.delete(waiter);
        waiter.resolve();
      } else if (state === "error" || state === "stopped") {
        if (waiter.timer) clearTimeout(waiter.timer);
        this.spinDownWaiters.delete(waiter);
        waiter.reject(
          new TrainerError(
            "control-rejected",
            state === "error" ? "Spin-down reported an error" : "Spin-down was stopped",
          ),
        );
      }
    }
  }

  private enqueueControl<T>(task: () => Promise<T>): Promise<T> {
    const run = this.controlQueue.then(task, task);
    this.controlQueue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private async ensureControlLocked(): Promise<void> {
    if (this.controlState === "granted") return;
    if (this.controlState === "unavailable" || !this.controlPoint) {
      throw new TrainerError("control-unavailable", "Trainer control is not available");
    }
    if (this.controlState === "failed") {
      throw new TrainerError(
        "control-unavailable",
        "Trainer control previously failed. Call requestControl() or reconnect.",
        { cause: this.lastError },
      );
    }

    this.controlState = "requesting";
    try {
      const response = await this.writeControl(encodeRequestControl());
      if (!response.success) {
        throw new TrainerControlError(
          response.requestOpcode,
          response.resultCode,
          response.result,
        );
      }
      this.controlState = "granted";
    } catch (error) {
      this.controlState = "failed";
      throw error;
    }
  }

  private async ensureStartedLocked(): Promise<void> {
    if (this.startAttempted) return;
    this.startAttempted = true;

    const profile =
      this.opcodeProfileOption === "brief"
        ? { name: "brief" as const, opcodes: FTMS_BRIEF_OPCODES }
        : { name: "standard" as const, opcodes: FTMS_STANDARD_OPCODES };
    this.opcodes = profile.opcodes;

    try {
      const response = await this.writeControl(encodeStart(profile.opcodes.start));
      if (!response.success) {
        throw new TrainerControlError(
          response.requestOpcode,
          response.resultCode,
          response.result,
        );
      }
      this.detectedOpcodeProfile = profile.name;
      this.machineState = "started";
    } catch (error) {
      // Start failing is not fatal: grade/power commands can still work.
      this.report(error);
    }
  }

  private async reapplyControlLocked(): Promise<void> {
    if (this.activeControl === "target-power" && this.lastTargetPower !== null) {
      await this.writeTargetPowerLocked(this.lastTargetPower);
      return;
    }
    if (this.activeControl === "resistance" && this.lastResistance !== null) {
      await this.writeResistanceLocked(this.lastResistance);
      return;
    }
    if (this.desiredGrade !== null) {
      await this.applyGradeLocked(this.desiredGrade);
    }
  }

  private async applyGradeLocked(grade: number): Promise<void> {
    if (this.capabilities.simulation !== "unsupported") {
      if (this.lastApplied?.mode === "sim" && sameNumber(this.lastApplied.value, grade)) return;
      try {
        const response = await this.writeControl(
          encodeSimParameters(grade, 0.004, 0.36, 0, this.opcodes.simulationParameters),
        );
        if (!response.success) {
          throw new TrainerControlError(
            response.requestOpcode,
            response.resultCode,
            response.result,
          );
        }
        this.activeControl = "sim";
        this.lastApplied = { mode: "sim", value: grade };
        this.simFailureCount = 0;
        this.capabilities.simulation = "supported";
        return;
      } catch (error) {
        this.report(error);
        this.simFailureCount += 1;
        if (
          error instanceof TrainerControlError &&
          (error.resultCode === 0x02 || error.resultCode === 0x03)
        ) {
          this.capabilities.simulation = "unsupported";
        } else if (this.simFailureCount >= 3) {
          this.capabilities.simulation = "unsupported";
        }
        this.log("Simulation parameters failed; using resistance fallback", error);
      }
    }

    const level = resistanceForGrade(grade);
    if (this.lastApplied?.mode === "resistance" && sameNumber(this.lastApplied.value, level)) {
      return;
    }
    try {
      await this.writeResistanceLocked(level);
      this.activeControl = "resistance";
      this.lastApplied = { mode: "resistance", value: level };
    } catch (error) {
      this.report(error);
    }
  }

  private async writeTargetPowerLocked(watts: number): Promise<void> {
    if (this.capabilities.targetPower === "unsupported") {
      throw new TrainerError(
        "procedure-unsupported",
        "This trainer does not support target power (0x05 standard)",
      );
    }
    const response = await this.writeControl(
      encodeTargetPower(watts, this.opcodes.targetPower),
    );
    if (!response.success) {
      if (response.resultCode === 0x02 || response.resultCode === 0x03) {
        this.capabilities.targetPower = "unsupported";
      }
      throw new TrainerControlError(
        response.requestOpcode,
        response.resultCode,
        response.result,
      );
    }
    this.capabilities.targetPower = "supported";
  }

  private async writeResistanceLocked(level: number): Promise<void> {
    if (this.capabilities.targetResistance === "unsupported") {
      throw new TrainerError(
        "procedure-unsupported",
        "This trainer does not support target resistance (0x04 standard)",
      );
    }
    const response = await this.writeControl(
      encodeTargetResistance(level, this.opcodes.targetResistance),
    );
    if (!response.success) {
      if (response.resultCode === 0x02 || response.resultCode === 0x03) {
        this.capabilities.targetResistance = "unsupported";
      }
      throw new TrainerControlError(
        response.requestOpcode,
        response.resultCode,
        response.result,
      );
    }
    this.capabilities.targetResistance = "supported";
  }

  private async writeControl(payload: Uint8Array): Promise<ControlPointResponse> {
    const characteristic = this.controlPoint;
    if (!characteristic) {
      throw new TrainerError("control-unavailable", "Trainer control point is not available");
    }
    if (!this.connected) {
      throw new TrainerError("not-connected", "Trainer is not connected");
    }
    if (this.pendingResponse) {
      throw new TrainerError(
        "control-rejected",
        "A control point response is already pending; commands must be serialized",
      );
    }

    const opcode = payload[0] ?? 0;
    const responsePromise = this.waitForResponse(opcode);
    // The response can arrive while writeValue is still resolving. Mark the
    // rejection as handled here so a failed write does not surface as an
    // unhandled rejection; a successful write returns and awaits it normally.
    responsePromise.catch(() => {});
    try {
      if (characteristic.writeValueWithResponse) {
        await characteristic.writeValueWithResponse(payload);
      } else if (characteristic.writeValue) {
        await characteristic.writeValue(payload);
      } else {
        throw new TrainerError("gatt", "The control point characteristic is not writable");
      }
    } catch (error) {
      this.cancelPendingResponse(opcode, error);
      if (error instanceof TrainerError) throw error;
      throw new TrainerError(
        "gatt",
        `Could not write control point opcode 0x${opcode.toString(16)}`,
        { cause: error },
      );
    }
    return responsePromise;
  }

  private waitForResponse(opcode: number): Promise<ControlPointResponse> {
    return new Promise<ControlPointResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pendingResponse?.opcode === opcode) {
          this.pendingResponse = null;
          reject(
            new TrainerError(
              "control-timeout",
              `No control point response for opcode 0x${opcode.toString(16)}`,
            ),
          );
        }
      }, this.controlTimeoutMs);
      this.pendingResponse = { opcode, resolve, reject, timer };
    });
  }

  private cancelPendingResponse(opcode: number, error: unknown): void {
    const pending = this.pendingResponse;
    if (!pending || pending.opcode !== opcode) return;
    clearTimeout(pending.timer);
    this.pendingResponse = null;
    pending.reject(error);
  }

  private clampGrade(gradePct: number): number {
    const min = Math.min(this.minGradePct, this.maxGradePct);
    const max = Math.max(this.minGradePct, this.maxGradePct);
    return Math.max(min, Math.min(max, gradePct));
  }

  private scheduleReconnect(): void {
    if (this.explicitDisconnect || this.reconnectTimer || this.connected) return;
    if (this.reconnectAttempt >= this.reconnectAttempts) {
      this.report(
        new TrainerError(
          "gatt",
          "Could not reconnect to the trainer. Close other Bluetooth apps and use Retry.",
        ),
      );
      this.emitConnection({ connected: false, reason: "reconnect-failed" });
      return;
    }
    const delay = Math.min(
      this.reconnectMaxDelayMs,
      this.reconnectBaseDelayMs * 2 ** this.reconnectAttempt,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.attemptReconnect();
    }, delay);
  }

  private async attemptReconnect(): Promise<void> {
    if (this.explicitDisconnect || this.connected) return;
    try {
      await this.runConnect("reconnect");
      this.reconnectAttempt = 0;
    } catch (error) {
      this.report(error);
      this.scheduleReconnect();
    }
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private cleanupLink(): void {
    if (this.pendingResponse) {
      clearTimeout(this.pendingResponse.timer);
      this.pendingResponse.reject(
        new TrainerError("not-connected", "Trainer disconnected while a command was pending"),
      );
      this.pendingResponse = null;
    }

    this.connected = false;
    this.gatt = null;
    this.indoorBikeData = null;
    this.controlPoint = null;
    this.statusCharacteristic = null;
    this.controlState = this.capabilities.control === "unsupported" ? "unavailable" : "idle";
    this.detectedOpcodeProfile = "unknown";
    this.opcodes = FTMS_STANDARD_OPCODES;
    this.startAttempted = false;
    this.machineState = "unknown";
    this.spinDownState = "unknown";
    this.simFailureCount = 0;
    this.lastApplied = null;

    for (const waiter of [...this.spinDownWaiters]) {
      if (waiter.timer) clearTimeout(waiter.timer);
      this.spinDownWaiters.delete(waiter);
      waiter.reject(new TrainerError("not-connected", "Trainer disconnected during spin-down"));
    }
  }

  private async stopNotifications(
    characteristic: BleCharacteristicLike | null,
  ): Promise<void> {
    if (!characteristic) return;
    try {
      await characteristic.stopNotifications();
    } catch (error) {
      this.log("Could not stop notifications", error);
    }
  }

  private report(error: unknown): TrainerError {
    const trainerError =
      error instanceof TrainerError
        ? error
        : new TrainerError("gatt", errorMessage(error), { cause: error });
    this.lastError = trainerError;
    this.log(trainerError.message, trainerError);
    for (const listener of [...this.errorListeners]) {
      try {
        listener(trainerError);
      } catch (listenerError) {
        this.log("Error listener threw", listenerError);
      }
    }
    return trainerError;
  }

  private emitConnection(event: TrainerConnectionEvent): void {
    for (const listener of [...this.connectionListeners]) {
      try {
        listener(event);
      } catch (error) {
        this.log("Connection listener threw", error);
      }
    }
  }

  private log(message: string, error?: unknown): void {
    try {
      this.logger(message, error);
    } catch {
      // A logger must never break the power stream.
    }
  }
}
